import { useMemo, useState } from "react";

import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";

import { PatientSummary } from "../../../types";
import TableViewShell from "../../../components/views/table-view-shell";

import PatientsTableRow from "./patients-table-row";

interface PatientsTableProps {
  filteredPatients: PatientSummary[];
  unfilteredPatientsCount: number;
}

type PatientSortField = "displayId" | "created" | "lastMeeting" | "nextMeeting";

const PatientsTable: React.FC<PatientsTableProps> = ({
  filteredPatients,
  unfilteredPatientsCount,
}) => {
  const [sortBy, setSortBy] = useState<PatientSortField>("nextMeeting");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const getMeetingTimestamp = (
    meeting: PatientSummary["lastMeeting"] | PatientSummary["nextMeeting"],
  ) => {
    return meeting?.startTime ? new Date(meeting.startTime).getTime() : null;
  };

  const sortedPatients = useMemo(() => {
    const sorted = [...filteredPatients];

    sorted.sort((a, b) => {
      let comparison = 0;

      if (sortBy === "displayId") {
        comparison = (a.displayId || "").localeCompare(b.displayId || "", undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }

      if (sortBy === "created") {
        comparison = new Date(a.created).getTime() - new Date(b.created).getTime();
      }

      if (sortBy === "lastMeeting") {
        const aTime = getMeetingTimestamp(a.lastMeeting);
        const bTime = getMeetingTimestamp(b.lastMeeting);

        if (aTime == null && bTime == null) {
          comparison = 0;
        } else if (aTime == null) {
          comparison = 1;
        } else if (bTime == null) {
          comparison = -1;
        } else {
          comparison = aTime - bTime;
        }
      }

      if (sortBy === "nextMeeting") {
        const aTime = getMeetingTimestamp(a.nextMeeting);
        const bTime = getMeetingTimestamp(b.nextMeeting);

        if (aTime == null && bTime == null) {
          comparison = 0;
        } else if (aTime == null) {
          comparison = 1;
        } else if (bTime == null) {
          comparison = -1;
        } else {
          comparison = aTime - bTime;
        }
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [filteredPatients, sortBy, sortOrder]);

  const handleSort = (field: PatientSortField) => {
    if (sortBy === field) {
      setSortOrder((currentOrder) =>
        currentOrder === "asc" ? "desc" : "asc",
      );
      return;
    }

    setSortBy(field);
    setSortOrder(field === "displayId" ? "asc" : "desc");
  };

  const headerLabelSx = { fontSize: "0.875rem" };
  const headerCellSx = { py: 0.5, fontSize: "0.875rem", whiteSpace: "nowrap" };

  // Header and body are separate tables sharing the same fixed column widths,
  // so only the body scrolls and its scrollbar starts below the header.
  const columns = (
    <colgroup>
      <col style={{ width: 150 }} />
      <col style={{ width: 110 }} />
      <col style={{ width: 190 }} />
      <col style={{ width: 220 }} />
      <col style={{ width: 220 }} />
      <col style={{ width: 170 }} />
    </colgroup>
  );

  return (
    <TableViewShell
      shownCount={sortedPatients.length}
      totalCount={unfilteredPatientsCount}
      columns={columns}
      headerAriaLabel="patient table header"
      bodyAriaLabel="patient table"
      header={
        <TableHead>
          <TableRow>
            <TableCell align="left" sx={headerCellSx}>
              <TableSortLabel
                active={sortBy === "displayId"}
                direction={sortBy === "displayId" ? sortOrder : "asc"}
                onClick={() => handleSort("displayId")}
                sx={headerLabelSx}
              >
                Patient ID
              </TableSortLabel>
            </TableCell>
            <TableCell align="left" sx={headerCellSx}>Status</TableCell>
            <TableCell align="left" sx={headerCellSx}>
              <TableSortLabel
                active={sortBy === "created"}
                direction={sortBy === "created" ? sortOrder : "desc"}
                onClick={() => handleSort("created")}
                sx={headerLabelSx}
              >
                Date Created
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={headerCellSx}>
              <TableSortLabel
                active={sortBy === "lastMeeting"}
                direction={sortBy === "lastMeeting" ? sortOrder : "desc"}
                onClick={() => handleSort("lastMeeting")}
                sx={headerLabelSx}
              >
                Last Meeting
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={headerCellSx}>
              <TableSortLabel
                active={sortBy === "nextMeeting"}
                direction={sortBy === "nextMeeting" ? sortOrder : "desc"}
                onClick={() => handleSort("nextMeeting")}
                sx={headerLabelSx}
              >
                Next Meeting
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={headerCellSx}></TableCell>
          </TableRow>
        </TableHead>
      }
      body={
        <TableBody>
          {sortedPatients.map((patient) => {
            return (
              <PatientsTableRow key={patient.displayId} patient={patient} />
            );
          })}
          {sortedPatients.length === 0 && (
            <TableRow key="no-patients">
              <TableCell
                colSpan={6}
                sx={{
                  paddingY: 2,
                }}
              >
                <Typography variant="body1">No patients found</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      }
    />
  );
};

export default PatientsTable;
